import { Chip } from "@nt/ui/chip";
import {
  BadgeDollarSign,
  Banknote,
  BookOpen,
  ChartLine,
  ChevronsUpDown,
  CircleUser,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  LogOut,
  Settings,
  Users,
  type LucideIcon,
} from "lucide-react";
import type * as React from "react";
import { Link, useLocation } from "react-router";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@nt/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@nt/ui/sidebar";
import type { Session } from "@/lib/queries";
import { signOut } from "@/lib/sign-out";

export const SUPPORT_EMAIL = "support@nonresident.tax";

export interface PartnerNavItem {
  title: string;
  to: string;
  icon: LucideIcon;
  group: "Program" | "Account";
  isActive: (pathname: string) => boolean;
}

const under = (base: string) => (p: string) =>
  p === base || p.startsWith(`${base}/`);

/** Every destination the sidebar offers, in nav order (spec: Information architecture). */
export const PARTNER_NAV: readonly PartnerNavItem[] = [
  {
    title: "Overview",
    to: "/",
    icon: LayoutDashboard,
    group: "Program",
    isActive: (p) => p === "/",
  },
  {
    title: "Performance",
    to: "/performance",
    icon: ChartLine,
    group: "Program",
    isActive: under("/performance"),
  },
  {
    title: "Links",
    to: "/links",
    icon: Link2,
    group: "Program",
    isActive: under("/links"),
  },
  {
    title: "Customers",
    to: "/customers",
    icon: Users,
    group: "Program",
    isActive: under("/customers"),
  },
  {
    title: "Payouts",
    to: "/payouts",
    icon: Banknote,
    group: "Program",
    isActive: under("/payouts"),
  },
  {
    title: "Rates",
    to: "/rates",
    icon: BadgeDollarSign,
    group: "Program",
    isActive: under("/rates"),
  },
  {
    title: "Resources",
    to: "/resources",
    icon: BookOpen,
    group: "Program",
    isActive: under("/resources"),
  },
  {
    title: "Settings",
    to: "/settings",
    icon: Settings,
    group: "Account",
    isActive: under("/settings"),
  },
];

const GROUPS = ["Program", "Account"] as const;

/** The name the partner chose, else their sign-in email. */
const displayNameOf = (session: Session) =>
  session.partner?.displayName || session.email;

function NavUser({ session }: { session: Session }) {
  const { isMobile } = useSidebar();
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger render={<SidebarMenuButton size="lg" />}>
            <CircleUser className="size-8 shrink-0 p-0.5" />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium text-foreground">
                {displayNameOf(session)}
              </span>
              <span className="truncate text-compact text-muted-foreground">
                {session.email}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            {/* GroupLabel throws outside a Group. */}
            <DropdownMenuGroup>
              <DropdownMenuLabel>
                Signed in as {session.email}
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void signOut()}>
              <LogOut className="size-4 text-muted-foreground" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function PartnerSidebar({
  session,
  setupStepsLeft,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  session: Session;
  /** Shows "1" on Settings while the payout method or a needed tax form is missing. */
  setupStepsLeft: number;
}) {
  const pathname = useLocation().pathname;
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <Sidebar variant="inset" collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<Link to="/" />}>
              <img
                src="/nonresidentTaxIcon.svg"
                alt=""
                width={32}
                height={32}
                className="size-8 shrink-0 rounded-control"
              />
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-semibold text-foreground">
                  Nonresident Tax
                </span>
                <span className="truncate text-compact text-muted-foreground">
                  Partner portal
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        {GROUPS.map((group) => (
          <SidebarGroup key={group}>
            <SidebarGroupLabel>{group}</SidebarGroupLabel>
            <SidebarMenu>
              {PARTNER_NAV.filter((item) => item.group === group).map(
                (item) => {
                  const active = item.isActive(pathname);
                  const badge = item.to === "/settings" && setupStepsLeft > 0;
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        isActive={active}
                        tooltip={item.title}
                        render={
                          <Link
                            to={item.to}
                            aria-current={active ? "page" : undefined}
                            onClick={() => {
                              if (isMobile) setOpenMobile(false);
                            }}
                          />
                        }
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                      {badge ? (
                        <SidebarMenuBadge
                          data-testid="nav-settings-badge"
                          aria-label={`${setupStepsLeft} ${setupStepsLeft === 1 ? "step" : "steps"} left before we can pay you`}
                          className="bg-warning-tint text-warning"
                        >
                          {setupStepsLeft}
                        </SidebarMenuBadge>
                      ) : null}
                    </SidebarMenuItem>
                  );
                },
              )}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        {session.state === "read_only" ? (
          <div className="px-2 group-data-[collapsible=icon]:hidden">
            <Chip tone="warning">Read-only</Chip>
          </div>
        ) : null}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Need help?"
              render={<a href={`mailto:${SUPPORT_EMAIL}`} />}
            >
              <LifeBuoy />
              <span>Need help?</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="max-md:hidden">
          <NavUser session={session} />
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
